/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * Left-hand action-button cluster for the Post Stream Review toolbar.
 *
 * Order: prev/next (segment nav, only when >1 segment) → undo/redo →
 *        view changes / take to staging → like / dislike feedback.
 */
import React from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';

import {
	renderArrowDownIcon,
	renderArrowUpIcon,
	renderDislikeIcon,
	renderEyeOpenIcon,
	renderLikeIcon,
	renderLinkExternalIcon,
	renderRedoIcon,
	renderUndoIcon,
} from './icons';
import { messages } from './messages';

/** Group of left-side action buttons. */
const buttonGroupCSS = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	flexShrink: 0,
});

/** Slim 1px vertical divider between button clusters. */
const dividerCSS = css({
	width: '1px',
	height: token('space.250'),
	backgroundColor: token('color.border'),
	marginLeft: token('space.075'),
	marginRight: token('space.075'),
	flexShrink: 0,
});

export type FeedbackValue = 'up' | 'down' | null;

export interface ActionButtonsProps {
	totalSegments: number;
	segmentIndex: number;
	canGoPrevious: boolean;
	canGoNext: boolean;
	onPrevious: () => void;
	onNext: () => void;
	canUndo: boolean;
	canRedo: boolean;
	onUndo: () => void;
	onRedo: () => void;
	canViewChanges: boolean;
	isDisplayingChanges: boolean;
	onViewChanges: () => void;
	canTakeToStaging: boolean;
	onTakeToStaging: () => void;
	feedback: FeedbackValue;
	onFeedbackUp: () => void;
	onFeedbackDown: () => void;
}

export function ActionButtons({
	totalSegments,
	segmentIndex,
	canGoPrevious,
	canGoNext,
	onPrevious,
	onNext,
	canUndo,
	canRedo,
	onUndo,
	onRedo,
	canViewChanges,
	isDisplayingChanges,
	onViewChanges,
	canTakeToStaging,
	onTakeToStaging,
	feedback,
	onFeedbackUp,
	onFeedbackDown,
}: ActionButtonsProps): JSX.Element {
	const intl = useIntl();
	const undoLabel = intl.formatMessage(messages.undo);
	const redoLabel = intl.formatMessage(messages.redo);
	const viewChangesLabel = intl.formatMessage(messages.viewChanges);
	const thumbsUpLabel = intl.formatMessage(messages.thumbsUp);
	const thumbsDownLabel = intl.formatMessage(messages.thumbsDown);
	const takeToStagingLabel = intl.formatMessage(messages.takeToStaging);
	const prevLabel = intl.formatMessage(messages.previousChange);
	const nextLabel = intl.formatMessage(messages.nextChange);

	// Note on `segmentIndex` usage: it's only read for `isDisabled` aria
	// affordances. The canonical "can navigate" boolean is `canGoPrevious /
	// canGoNext` from the segment-navigation hook. We keep both so the
	// disabled state survives even if the hook later memoises differently.
	const isPrevDisabled = !canGoPrevious || segmentIndex <= 0;
	const isNextDisabled = !canGoNext || segmentIndex >= totalSegments - 1;

	return (
		<div css={buttonGroupCSS}>
			{totalSegments > 1 ? (
				<React.Fragment>
					<IconButton
						appearance="subtle"
						icon={renderArrowUpIcon}
						label={prevLabel}
						isDisabled={isPrevDisabled}
						onClick={onPrevious}
						testId="post-stream-review-prev"
					/>
					<IconButton
						appearance="subtle"
						icon={renderArrowDownIcon}
						label={nextLabel}
						isDisabled={isNextDisabled}
						onClick={onNext}
						testId="post-stream-review-next"
					/>
					<div css={dividerCSS} aria-hidden />
				</React.Fragment>
			) : null}
			<IconButton
				appearance="subtle"
				icon={renderUndoIcon}
				label={undoLabel}
				isDisabled={!canUndo}
				onClick={onUndo}
				testId="post-stream-review-undo"
			/>
			<IconButton
				appearance="subtle"
				icon={renderRedoIcon}
				label={redoLabel}
				isDisabled={!canRedo}
				onClick={onRedo}
				testId="post-stream-review-redo"
			/>
			<div css={dividerCSS} aria-hidden />
			<IconButton
				// Toggled state: `primary` when the diff overlay is visible,
				// `subtle` otherwise — matches the visual language of other
				// toggled toolbar buttons in the editor.
				appearance={isDisplayingChanges ? 'primary' : 'subtle'}
				icon={renderEyeOpenIcon}
				label={viewChangesLabel}
				isDisabled={!canViewChanges}
				onClick={onViewChanges}
				testId="post-stream-review-view-changes"
			/>
			<IconButton
				appearance="subtle"
				icon={renderLinkExternalIcon}
				label={takeToStagingLabel}
				isDisabled={!canTakeToStaging}
				onClick={onTakeToStaging}
				testId="post-stream-review-take-to-staging"
			/>
			<div css={dividerCSS} aria-hidden />
			<IconButton
				appearance={feedback === 'up' ? 'primary' : 'subtle'}
				icon={renderLikeIcon}
				label={thumbsUpLabel}
				onClick={onFeedbackUp}
				testId="post-stream-review-feedback-up"
			/>
			<IconButton
				appearance={feedback === 'down' ? 'primary' : 'subtle'}
				icon={renderDislikeIcon}
				label={thumbsDownLabel}
				onClick={onFeedbackDown}
				testId="post-stream-review-feedback-down"
			/>
			<div css={dividerCSS} aria-hidden />
		</div>
	);
}
